import React, { useEffect, useMemo, useRef, useState } from "react";
import { eachDayOfInterval, format } from "date-fns";
import {
  getTimelineWeekEnd,
  getTimelineWeekStart,
  normalizeTimelineDate,
  TIMELINE_DATE_COLUMN_WIDTH,
} from "../../../contexts/TimelineContext";
import {
  getTimelineDayState,
  groupTimelineDatesByWeek,
  TimelineDateHeader,
  TimelineShowTodayButton,
} from "../TimelineParts/TimelineCalendarUi";
import { getCurrentPhase } from "./clientCampaignStatus";
import ClientCampaignTimelineSidebar from "./ClientCampaignTimelineSidebar";
import ClientCampaignBar from "./ClientCampaignBar";

const DATE_COL_W = TIMELINE_DATE_COLUMN_WIDTH;
const ROW_HEIGHT = 48;
const WEEK_HEADER_HEIGHT = 40;
const DATE_HEADER_HEIGHT = 48;
// Must match sum of calendar header rows so sidebar campaign rows align with Gantt rows.
const CALENDAR_HEADER_HEIGHT = WEEK_HEADER_HEIGHT + DATE_HEADER_HEIGHT;

// Like timelineDayBackgroundClass but uses bg-rb-grey for regular days
// so the entire Gantt area has a unified grey background (sidebar stays white).
function clientDayBgClass({ isToday, isNonWorkingDay }) {
  if (isToday) return "bg-rb-green/20 dark:bg-rb-green/15";
  if (isNonWorkingDay)
    return "bg-rb-surface-muted dark:bg-rb-dark-mode-background";
  return "bg-rb-grey dark:bg-rb-dark-mode";
}

function toDateStr(val) {
  if (!val) return null;
  const raw = val && typeof val === "object" ? val.utc : String(val);
  return raw ? raw.split("T")[0].split(" ")[0] : null;
}

function computeDateRange(campaigns) {
  // Use phase dates when available so all bars are within the generated date range.
  const allDateStrs = campaigns.flatMap((c) => {
    const phases = c.project_phases || [];
    if (phases.length) {
      return phases.flatMap((p) => [
        toDateStr(p.start_date),
        toDateStr(p.end_date),
      ]);
    }
    return [toDateStr(c.start_date), toDateStr(c.end_date)];
  });

  const allDates = allDateStrs
    .filter(Boolean)
    .map((s) => normalizeTimelineDate(s))
    .filter(Boolean);

  if (!allDates.length) {
    const now = new Date();
    return {
      start: getTimelineWeekStart(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14),
      ),
      end: getTimelineWeekEnd(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() + 28),
      ),
    };
  }

  const min = allDates.reduce((a, b) => (a < b ? a : b));
  const max = allDates.reduce((a, b) => (a > b ? a : b));
  return { start: getTimelineWeekStart(min), end: getTimelineWeekEnd(max) };
}

export default function ClientCampaignTimeline({
  campaigns = [],
  endedCampaigns = [],
  title = "Campaigns",
  hideCampaignLink = false,
}) {
  const scrollRef = useRef(null);
  const [scrolledToday, setScrolledToday] = useState(false);

  const { dates, groupedDates, rangeStart, rangeEnd } = useMemo(() => {
    const { start, end } = computeDateRange(campaigns);
    const dates = eachDayOfInterval({ start, end });
    return {
      dates,
      groupedDates: groupTimelineDatesByWeek(dates),
      rangeStart: start,
      rangeEnd: end,
    };
  }, [campaigns]);

  const scrollToDate = (dateStr) => {
    if (!scrollRef.current || !dates.length) return;
    const idx = dates.findIndex((d) => format(d, "yyyy-MM-dd") === dateStr);
    if (idx !== -1) {
      const pos =
        idx * DATE_COL_W - scrollRef.current.clientWidth / 2 + DATE_COL_W / 2;
      scrollRef.current.scrollLeft = Math.max(0, pos);
    }
  };

  const scrollToToday = () => scrollToDate(format(new Date(), "yyyy-MM-dd"));

  useEffect(() => {
    if (scrolledToday || !dates.length || !scrollRef.current) return;
    const frame = requestAnimationFrame(scrollToToday);
    setScrolledToday(true);
    return () => cancelAnimationFrame(frame);
  }, [dates, scrolledToday]);

  const handleCampaignClick = (campaign) => {
    const phase = getCurrentPhase(campaign);
    const dateStr = toDateStr(phase?.start_date || campaign.start_date);
    if (dateStr) scrollToDate(dateStr);
  };

  // Resolves the phases to render as bars for one campaign row.
  // Multiple assets in the same phase group (e.g. "Draft Phase" + "Go-Live Phase" both → main)
  // are merged into a single bar spanning min(start) → max(end).
  // Falls back to a single bar using campaign dates if no phases are defined.
  const getCampaignPhases = (campaign) => {
    const rawPhases = campaign.project_phases || [];

    if (!rawPhases.length) {
      const start = toDateStr(campaign.start_date);
      const end = toDateStr(campaign.end_date);
      if (!start || !end) return [];
      return [
        {
          phase: "main",
          theme_color: campaign.theme_color,
          start_date: start,
          end_date: end,
        },
      ];
    }

    const merged = {};
    rawPhases.forEach((p) => {
      if (!p.start_date) return;
      if (!merged[p.phase]) {
        merged[p.phase] = {
          phase: p.phase,
          theme_color: p.theme_color,
          start_date: p.start_date,
          end_date: p.end_date,
        };
      } else {
        const m = merged[p.phase];
        if (p.start_date < m.start_date) m.start_date = p.start_date;
        if (p.end_date && (!m.end_date || p.end_date > m.end_date))
          m.end_date = p.end_date;
      }
    });

    return Object.values(merged);
  };

  const getBarStyle = (startDate, endDate) => {
    const startStr = toDateStr(startDate);
    const endStr = toDateStr(endDate);
    if (!startStr || !endStr) return null;

    const startIdx = dates.findIndex(
      (d) => format(d, "yyyy-MM-dd") === startStr,
    );
    const endIdx = dates.findIndex((d) => format(d, "yyyy-MM-dd") === endStr);
    const s = startIdx >= 0 ? startIdx : 0;
    const e = endIdx >= 0 ? endIdx : dates.length - 1;

    return {
      left: s * DATE_COL_W + 2,
      width: Math.max((e - s + 1) * DATE_COL_W - 4, DATE_COL_W - 4),
    };
  };

  const totalWidth = dates.length * DATE_COL_W;

  return (
    <div className="flex h-full overflow-hidden">
      <ClientCampaignTimelineSidebar
        campaigns={campaigns}
        endedCampaigns={endedCampaigns}
        title={title}
        headerHeight={CALENDAR_HEADER_HEIGHT}
        rowHeight={ROW_HEIGHT}
        onCampaignClick={handleCampaignClick}
        hideCampaignLink={hideCampaignLink}
      />

      {/* Outer wrapper is the positioning context for the Show Today button */}
      <div className="relative min-w-0 flex-1">
        <div
          ref={scrollRef}
          className="h-full overflow-x-auto bg-rb-grey dark:bg-rb-dark-mode-background"
        >
          <div
            style={{ width: totalWidth, minHeight: "100%" }}
            className="flex flex-col"
          >
            {/* Week header row */}
            <div
              className="flex flex-shrink-0 border-b border-rb-widget-border bg-rb-grey dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode"
              style={{ height: WEEK_HEADER_HEIGHT }}
            >
              {groupedDates.map((weekDates) => (
                <div
                  key={format(weekDates[0], "yyyy-MM-dd")}
                  style={{ width: weekDates.length * DATE_COL_W }}
                  className="flex flex-shrink-0 items-center justify-center whitespace-nowrap border-r border-rb-widget-border px-2 text-sm font-semibold dark:border-rb-dark-mode-lighter dark:text-white"
                >
                  {format(getTimelineWeekStart(weekDates[0]), "MMM d")}
                  {" – "}
                  {format(getTimelineWeekEnd(weekDates[0]), "MMM d, yyyy")}
                </div>
              ))}
            </div>

            {/* Date header row */}
            <div className="flex flex-shrink-0 border-b border-rb-widget-border bg-rb-grey dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode">
              {dates.map((date) => (
                <div
                  key={format(date, "yyyy-MM-dd")}
                  style={{
                    width: DATE_COL_W,
                    height: DATE_HEADER_HEIGHT,
                    flexShrink: 0,
                  }}
                  className="border-r border-rb-widget-border dark:border-rb-dark-mode-lighter"
                >
                  <TimelineDateHeader date={date} />
                </div>
              ))}
            </div>

            {/* Campaign rows */}
            <div className="relative flex-1">
              {/* Background column grid */}
              <div className="pointer-events-none absolute inset-0 flex">
                {dates.map((date) => (
                  <div
                    key={format(date, "yyyy-MM-dd")}
                    style={{ width: DATE_COL_W, flexShrink: 0 }}
                    className={`h-full border-r border-rb-widget-border dark:border-rb-dark-mode-lighter ${clientDayBgClass(getTimelineDayState(date, null))}`}
                  />
                ))}
              </div>

              {campaigns.length === 0 && (
                <div className="flex h-40 items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                  No active campaigns to display
                </div>
              )}

              {campaigns.map((campaign) => {
                const phases = getCampaignPhases(campaign);
                return (
                  <div
                    key={campaign.id}
                    style={{ height: ROW_HEIGHT }}
                    className="relative border-b border-rb-widget-border dark:border-rb-dark-mode-lighter"
                  >
                    {phases.map((phase, idx) => {
                      const barStyle = getBarStyle(
                        phase.start_date,
                        phase.end_date,
                      );
                      if (!barStyle) return null;
                      return (
                        <ClientCampaignBar
                          key={`${phase.phase}-${idx}`}
                          campaign={campaign}
                          phase={phase}
                          style={barStyle}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <TimelineShowTodayButton
          onClick={scrollToToday}
          className="absolute bottom-4 right-4 z-30"
          startDate={rangeStart}
          endDate={rangeEnd}
        />
      </div>
    </div>
  );
}
